import React from 'react';
import { Link } from 'react-router-dom';
import {
  CONSTRUX_CATALOG,
  catalogLabel,
  catalogOrigin,
  softplanExamplesCatalog,
} from '../../data/sitePlannerCatalog';
import { formatFt } from '../../utils/unitsUs';

function Readout({ k, v, u }) {
  return (
    <div className="readout">
      <div className="k">{k}</div>
      <div className="v">
        {v}
        {u ? <span className="u">{u}</span> : null}
      </div>
    </div>
  );
}

/**
 * Design side panel — catalog place buttons + stack selection.
 * See docs/XLABS_DESIGN.md
 */
export default function DesignSidePanel({
  site,
  floorY,
  siteContentReady,
  catalogType,
  onCatalogTypeChange,
  onAddStructure,
  structures,
  selectedId,
  onSelectStructure,
  onRemoveStructure,
  tool,
}) {
  const softplan = softplanExamplesCatalog();
  const selected = structures.find((s) => s.id === selectedId);
  const floorReady = floorY != null && Number.isFinite(floorY);
  const canPlace = Boolean(site && siteContentReady);

  const placeModel = (slug) => {
    if (!slug) return;
    onCatalogTypeChange(slug);
    if (!canPlace) return;
    onAddStructure(slug);
  };

  return (
    <aside className="side">
      <div className="side-head">
        <div className="side-h">step 02 · design</div>
        <div className="side-title">Structures</div>
        <div className="side-sub">
          Tap a model to place it on the lot. Select from the stack to move it.
          {selected
            ? ` · ${catalogLabel(selected.type)} selected`
            : structures.length
              ? ` · ${structures.length} placed`
              : ''}
        </div>
      </div>

      <div className="side-body">
        <div className="side-section">
          <div className="readouts">
            <Readout k="structures" v={structures.length} u="" />
            <Readout
              k="selected"
              v={selected ? catalogLabel(selected.type) : '—'}
              u=""
            />
            <Readout
              k="site"
              v={site ? (floorReady ? 'ready' : 'booting') : 'none'}
              u=""
            />
            <Readout
              k="floor"
              v={floorReady ? formatFt(floorY) : '—'}
              u=""
            />
          </div>
        </div>

        <div className="side-section">
          <div className="sec-h">exterior spec</div>
          <a
            href="#exterior"
            className="ext-cta"
            title="Coming soon"
            onClick={(e) => e.preventDefault()}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
              <span style={{ fontWeight: 500, fontSize: 13 }}>Open exterior</span>
              <span
                style={{
                  marginLeft: 'auto',
                  color: 'var(--mid)',
                  fontFamily: 'var(--font-mono)',
                  fontSize: 11,
                }}
              >
                soon
              </span>
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: 'var(--ink-2)',
                marginTop: 4,
                lineHeight: 1.5,
              }}
            >
              Cladding · roof · trim · windows · doors — deferred until the
              exterior sheet is ported.
            </div>
          </a>
        </div>

        <div className="side-section" id="design-catalog">
          <div className="sec-h">catalog · Construx</div>
          <p className="catalog-hint">Tap a model to place it</p>
          <div className="catalog-chip-row" role="list">
            {CONSTRUX_CATALOG.map((m) => (
              <button
                key={m.slug}
                type="button"
                role="listitem"
                className={`chip catalog-place-btn${
                  catalogType === m.slug ? ' sel' : ''
                }`}
                disabled={!canPlace}
                title={
                  canPlace
                    ? `Place ${m.label} on the lot`
                    : site
                      ? 'Waiting for floor lock…'
                      : 'Load a site on Locate first'
                }
                onClick={() => placeModel(m.slug)}
              >
                <span className="catalog-place-btn__label">{m.label}</span>
                <span className="catalog-place-btn__action">place</span>
              </button>
            ))}
          </div>
          {softplan.length > 0 && (
            <>
              <div className="sec-h" style={{ marginTop: 12 }}>
                SoftPlan examples
              </div>
              <select
                value={
                  catalogOrigin(catalogType) === 'softplan' ? catalogType : ''
                }
                onChange={(e) => {
                  if (e.target.value) placeModel(e.target.value);
                }}
                disabled={!canPlace}
                aria-label="Place SoftPlan example model"
                style={{
                  width: '100%',
                  marginTop: 6,
                  padding: '10px 10px',
                  minHeight: 44,
                  fontSize: 12,
                  fontFamily: 'var(--font-mono)',
                  border: '1px solid var(--line)',
                  background: 'var(--panel)',
                  color: 'var(--ink)',
                  borderRadius: 0,
                }}
              >
                <option value="">Place SoftPlan example…</option>
                {softplan.map((m) => (
                  <option key={m.slug} value={m.slug}>
                    {m.label}
                  </option>
                ))}
              </select>
            </>
          )}
          <button
            type="button"
            className="button-secondary"
            style={{ marginTop: 12, width: '100%', minHeight: 44 }}
            disabled={!canPlace}
            onClick={() => {
              if (!canPlace) return;
              onAddStructure(catalogType);
            }}
          >
            Add another{' '}
            {catalogOrigin(catalogType) === 'softplan'
              ? `SP · ${catalogLabel(catalogType)}`
              : catalogLabel(catalogType)}
          </button>
          {tool === 'place' && (
            <div
              style={{
                marginTop: 8,
                fontFamily: 'var(--font-mono)',
                fontSize: 10.5,
                color: 'var(--mid)',
              }}
            >
              drops at lot centroid · drag gizmo to move
            </div>
          )}
          {!site && (
            <div className="field-error" style={{ marginTop: 8 }}>
              Load a site on Locate first.
            </div>
          )}
          {site && !siteContentReady && (
            <div className="field-error" style={{ marginTop: 8 }}>
              Waiting for floor lock…
            </div>
          )}
        </div>

        <div className="side-section">
          <div className="sec-h">stack · {structures.length}</div>
          {structures.length === 0 ? (
            <div
              style={{
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--mid)',
              }}
            >
              No structures yet — tap a catalog model above.
            </div>
          ) : (
            <ul className="stack-list">
              {structures.map((s, i) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className={`stack-select${
                      selectedId === s.id ? ' sel' : ''
                    }`}
                    aria-pressed={selectedId === s.id}
                    onClick={() => onSelectStructure(s.id)}
                  >
                    <span className="swatch" aria-hidden />
                    <span className="stack-select__name">
                      {catalogOrigin(s.type) === 'softplan' ? 'SP · ' : ''}
                      {catalogLabel(s.type)} #{i + 1}
                    </span>
                    <span className="stack-select__hint">
                      {selectedId === s.id ? 'selected' : 'select'}
                    </span>
                  </button>
                  <button
                    type="button"
                    className="stack-remove"
                    onClick={() => onRemoveStructure(s.id)}
                  >
                    remove
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div
          className="side-section"
          style={{ borderBottom: 0, display: 'flex', gap: 8 }}
        >
          <Link
            className="button-secondary"
            to="/xlabs/locate"
            style={{ flex: 1, textAlign: 'center', minHeight: 44 }}
          >
            ← locate
          </Link>
          <Link
            className="button-primary"
            to="/xlabs-hub"
            style={{ flex: 1, textAlign: 'center', minHeight: 44 }}
          >
            analyse →
          </Link>
        </div>
      </div>
    </aside>
  );
}
